import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { api } from "@/helper/api";
import DropDown from "@/components/forms/inputs/dropDown";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { useConfirmationModal } from "@/context/confirmationModalContext";
import { useModalDismiss } from "@/helper/customHooks/useModalDismiss";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId?: string;
  companySubjectId?: string;
  selectedUsers: any[];
  notify: (message: string, variant: "success" | "error") => void;
  onError: (error: any) => void;
  onAdded?: () => void;
}

export default function BulkDepartmentDialog(props: Props) {
  const { t } = useTranslation();
  const { showConfirmationModalAsync } = useConfirmationModal();
  const [departmentList, setDepartmentList] = useState<any[]>([]);
  const [selectedDepartment, setSelectedDepartment] = useState<any[]>([]);
  const cancelClose = useModalDismiss(props.open, () =>
    props.onOpenChange(false)
  );

  const numberOfSelectedUsers = props.selectedUsers.length;

  const fetchDepartments = () => {
    const additionalUrlParameters = {
      include_user_count: true,
      depth: "0",
    };

    if (props.companyId) {
      (additionalUrlParameters as any)["impersonate_subject"] =
        props.companySubjectId;
    }

    api.genericApiRequest({
      entity: "department",
      method: "get",
      parametersToRender: additionalUrlParameters,
      getAllPages: true,
      successHandler: (res: any) => {
        const departments = res.data.results.map((dept: any) => ({
          value: dept.id,
          label: dept.name,
        }));
        departments.sort((a: any, b: any) => a.label.localeCompare(b.label));
        setDepartmentList(departments);
      },
      failHandler: (error: any) => {
        props.onError(error);
        props.notify(t("error_fetching_departments"), "error");
      },
    });
  };

  useEffect(() => {
    if (props.open) {
      fetchDepartments();
    }
  }, [props.open]);

  const handleBulkAddUsersToDepartments = async (selectedUsers: any[]) => {
    if (!selectedDepartment || selectedDepartment.length === 0) {
      props.onError(t("select_at_least_one_department"));
      return;
    }

    const departmentNames = selectedDepartment
      .map(
        (deptId) => departmentList.find((dept) => dept.value === deptId)?.label
      )
      .join(", ");

    const userNames = selectedUsers
      .map((user) => `${user.first_name} ${user.last_name}`)
      .join(", ");

    const confirmMessage =
      selectedUsers.length < 10
        ? `${t("bulk_add_users_to_department_question", {
            departmentNames,
            numberOfSelectedUsers,
          })} (${userNames})`
        : t("bulk_add_many_users_to_department_question", {
            departmentNames,
            numberOfSelectedUsers,
          });
    const isConfirmed = await showConfirmationModalAsync(confirmMessage);

    if (!isConfirmed) return;

    try {
      const promises = selectedUsers.map((user) => {
        return new Promise<void>((resolve, reject) => {
          const updatedDepartmentSet = Array.from(
            new Set([
              ...(user.department_set || []).map((dept: any) => dept.id),
              ...selectedDepartment,
            ])
          );

          const result = {
            department_set: updatedDepartmentSet,
          };

          api.genericApiRequest({
            entity: "user",
            method: "put",
            entityId: user.id,
            submitData: result,
            successHandler: () => resolve(),
            failHandler: (error: any) => reject(error),
          });
        });
      });

      await Promise.all(promises).then(() => {
        props.notify(t("users_successfully_added_to_department"), "success");
        props.onAdded?.();
      });
    } catch (error) {
      props.onError(error);
      props.notify(t("bulk_add_to_department_failed"), "error");
    }
  };

  return (
    <Dialog open={props.open} fullWidth={true} {...cancelClose}>
      <DialogTitle textAlign={"center"}>
        {t("add_users_to_department")}
      </DialogTitle>
      <DialogContent>
        <DropDown
          name="department"
          placeholder={t("select_departments")}
          isMulti={true}
          options={departmentList}
          onChange={(selectedOptions: any) => {
            const selectedValues = selectedOptions.map(
              (option: any) => option.value
            );
            setSelectedDepartment(selectedValues);
          }}
          value={selectedDepartment.map(
            (id) =>
              departmentList.find((department) => department.value === id) || {
                value: id,
                label: id,
              }
          )}
        />
        <DialogActions
          sx={{
            justifyContent: "space-between",
            padding: "0",
            marginTop: "0.5rem",
          }}
        >
          <GenericButton
            variant="outlined"
            onClick={() => props.onOpenChange(false)}
          >
            {t("cancel")}
          </GenericButton>

          <GenericButton
            onClick={() => {
              if (props.selectedUsers.length > 0) {
                handleBulkAddUsersToDepartments(props.selectedUsers);
              }
              props.onOpenChange(false);
            }}
          >
            {t("add")}
          </GenericButton>
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
}
